import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Volume2, VolumeX, ArrowLeft,
  Loader2, Sparkles, Trash2,
} from 'lucide-react';
import { useAvas } from '../hooks/useDashboardData';
import { TEEN_VOICES } from './avaLibrary/data';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import useEmpathicHandover from '../hooks/useEmpathicHandover';
import SafetyNetModal from '../components/SafetyNetModal';
import { AvALightbox } from '../components/chat/AvALightbox';
import { ShareAvaModal } from '../components/chat/ShareAvaModal';
import ChatAvaSidebar from '../components/chat/ChatAvaSidebar';
import ChatAvaDisplay from '../components/chat/ChatAvaDisplay';
import ChatTranscript from '../components/chat/ChatTranscript';
import NeoJourneyBanner from '../components/chat/NeoJourneyBanner';
import ChatComposer from '../components/chat/ChatComposer';
import MicCountdownOverlay from '../components/chat/MicCountdownOverlay';
import { VoiceMinutesBadge } from '../components/chat/VoiceMinutesBadge';
import { useAudioManager } from '../hooks/useAudioManager';
import { useAvAVoicePlayback } from '../hooks/useAvAVoicePlayback';
import { useChatSend } from '../hooks/useChatSend';
import { useChatSession } from '../hooks/useChatSession';
import { useIdleLoopUrl } from '../hooks/useIdleLoopUrl';
import { useMomentVideo } from '../hooks/useMomentVideo';
import { toast } from 'sonner';

import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import VoiceNoteCard from '../components/engagement/VoiceNoteCard';
import PageMeta from '../components/PageMeta';
import SharePracticeModal from '../components/SharePracticeModal';
import MilestoneCelebrationToast from '../components/teen/MilestoneCelebrationToast';
import { MessageCircle } from 'lucide-react';
// Canonical moment texts — MUST byte-match the backend warm-up strings in
// app/api/ava_video.py so the pre-generated clips serve from cache.
const MOMENT_MILESTONE_TEXT = "You did it — I'm so proud of you! That took real courage, and you should feel brilliant about yourself. Keep going!";
const momentHandoverText = () => "Asking to talk to someone is a really brave step. I've opened a window for you — choose how you're doing, then pick a free helpline where a kind, real person is ready to listen. Take your time — I'll be right here.";


const API_URL = process.env.REACT_APP_BACKEND_URL;

// Slugify an AvA name for shareable URLs (e.g. "Maya" → "maya", "Dr. Ben" → "dr-ben").
const avaSlug = (name) => (name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const ConversationsChat = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { avaSlug: urlAvaSlug } = useParams();
  const { avas, loading: avasLoading } = useAvas();
  const { user } = useAuth();
  const [selectedAva, setSelectedAva] = useState(location.state?.selectedAva || null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showGreetingVideo, setShowGreetingVideo] = useState(true);
  const [showAvaLightbox, setShowAvaLightbox] = useState(false);
  const [interactionMode, setInteractionMode] = useState('voice'); // 'voice' default (Oct 2026 founder requirement) | 'text' | 'text_voice'
  const [copiedMsgId, setCopiedMsgId] = useState(null);
  // Sprint 207P — legacy 2-second mic countdown ref. Retained so the
  // suspended UI hook can still be re-enabled without state re-plumbing.
  const [micCountdown, setMicCountdown] = useState(null);
  // Sprint 207CC — show a typing indicator during the LLM intro wait so
  // the screen never sits blank while Maya warms up.
  const [avaTyping, setAvaTyping] = useState(false);
  // Sprint 207P — delete-conversation confirm modal.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Fix 2 — which tutor the delete modal targets (defaults to selectedAva).
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Fix 2 — per-AvA last-message snippets; also defines "active" tutors.
  const [convByAva, setConvByAva] = useState(null);
  const micCountdownAbortRef = useRef(false);
  // Feb 2026 — centralised audio lifecycle. `audioRef` continues to
  // expose the active <audio> element, but mounting / unmounting +
  // beforeunload + pagehide are all owned by the hook so the AvA's
  // voice can never leak across a route change.
  const { audioRef, attach: attachAudio, stop: stopAllAudio, isMounted: isAudioMounted } = useAudioManager();

  // 2026-07-03 refactor — TTS + word-reveal live in their own hook so
  // this page stays under 1,500 lines. Behaviour unchanged.
  const {
    isSpeaking,
    revealingMsgId,
    revealedWords,
    setRevealingMsgId,
    setRevealedWords,
    setIsSpeaking,
    ttsPlayedIds,
    revealIntervalRef,
    playTTS,
    stopSpeaking,
  } = useAvAVoicePlayback({ selectedAva, audioRef, attachAudio, stopAllAudio, isAudioMounted });

  const messagesEndRef = useRef(null);
  const greetedAvaRef = useRef(null); // Tracks which ava_id has been greeted — survives StrictMode remount

  // Feb 2026 refactor — idle-loop-video fetch lives in its own hook.
  const idleLoopUrl = useIdleLoopUrl(selectedAva?.ava_id);

  // Jun 2026 — Option A lip-sync moments (greeting / reveal / farewell).
  const [playingFarewell, setPlayingFarewell] = useState(false);
  const [revealPending, setRevealPending] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => {
    try {
      setRevealPending(
        selectedAva?.ava_id
          ? localStorage.getItem(`hibff_reveal_pending_${selectedAva.ava_id}`) === '1'
          : false,
      );
    } catch (e) { setRevealPending(false); }
  }, [selectedAva?.ava_id]);
  // Guard against the AvA-switch race: until the NEW session is created,
  // currentSession still belongs to the previous AvA — its cached clips must
  // never play on the newly selected AvA's tile.
  const sessionMatchesAva = currentSession?.ava_id === selectedAva?.ava_id;
  const [milestoneArmed, setMilestoneArmed] = useState(false);
  const [milestoneRun, setMilestoneRun] = useState(0);
  const [handoverArmed, setHandoverArmed] = useState(false);
  const [handoverRun, setHandoverRun] = useState(0);
  const greetingMoment = useMomentVideo(
    currentSession?.session_id,
    'greeting',
    showGreetingVideo && !isMuted && sessionMatchesAva,
    0,
    selectedAva?.ava_id,
  );
  const revealMoment = useMomentVideo(selectedAva?.ava_id ? `reveal_${selectedAva.ava_id}` : null, 'reveal', revealPending && !isMuted, 0, selectedAva?.ava_id);
  const farewellMoment = useMomentVideo(
    currentSession?.session_id,
    'farewell',
    !isMuted && sessionMatchesAva,
    0,
    selectedAva?.ava_id,
  );
  const milestoneMoment = useMomentVideo(currentSession?.session_id, 'milestone', milestoneArmed && !isMuted && sessionMatchesAva, milestoneRun, selectedAva?.ava_id);
  const handoverMoment = useMomentVideo(currentSession?.session_id, 'handover', handoverArmed && !isMuted && sessionMatchesAva, handoverRun, selectedAva?.ava_id);

  // Fire a lip-sync moment for the current session (cached clips are instant).
  const fireMoment = (momentType, text) => {
    if (!currentSession?.session_id || !selectedAva?.ava_id) return;
    const slot = TEEN_VOICES.find(v => v.id === (selectedAva?.avatar_config?.voice || ''));
    apiClient.request('/api/ava-video/trigger-moment', {
      method: 'POST',
      body: JSON.stringify({
        session_id: currentSession.session_id,
        moment_type: momentType,
        text,
        voice_id: slot?.baseVoice || selectedAva?.avatar_config?.voice || 'nova',
        ava_library_id: selectedAva.avatar_config?.ava_library_id || null,
        ava_id: selectedAva.ava_id,
      }),
    }).then((res) => {
      // Clean fallback: cap hit / no idle loop → stop polling instantly so
      // the TTS voice over the idle loop carries the moment with no waiting.
      if (res?.status === 'limit_reached' || res?.status === 'no_idle_loop') {
        if (momentType === 'handover') setHandoverArmed(false);
        if (momentType === 'milestone') setMilestoneArmed(false);
      }
    }).catch(() => {});
  };

  // Fix D — who speaks the current greeting: 'holding' | 'clip' | 'tts'.
  const speechOwnerRef = useRef(null);

  const momentVideoUrl = playingFarewell
    ? farewellMoment.videoUrl
    : (!isMuted && sessionMatchesAva
        ? (handoverMoment.videoUrl
            || (!isSpeaking
              ? (milestoneMoment.videoUrl
                  // Fix D — single-speaker invariant: once TTS owns the
                  // greeting, a late-arriving clip never mounts (it is kept
                  // in lastGreetingUrlRef for replay/share only).
                  || (speechOwnerRef.current !== 'tts' ? greetingMoment.videoUrl : null))
              : null))
        : null);

  // Keep the last greeting clip around for "Share my AvA" — reset on AvA
  // switch so Share never sends another AvA's clip.
  const lastGreetingUrlRef = useRef(null);
  useEffect(() => {
    lastGreetingUrlRef.current = null;
    speechOwnerRef.current = null;
  }, [selectedAva?.ava_id]);
  useEffect(() => {
    if (greetingMoment.videoUrl && sessionMatchesAva) lastGreetingUrlRef.current = greetingMoment.videoUrl;
  }, [greetingMoment.videoUrl, sessionMatchesAva]);

  const handleMomentEnd = () => {
    if (playingFarewell) {
      setPlayingFarewell(false);
      farewellMoment.dismiss();
      if (messages.length > 2) setShowNudge(true); else navigate('/conversations');
      return;
    }
    if (handoverMoment.videoUrl) {
      handoverMoment.dismiss();
      setHandoverArmed(false);
      return;
    }
    if (milestoneMoment.videoUrl) {
      milestoneMoment.dismiss();
      setMilestoneArmed(false);
      return;
    }
    if (greetingMoment.videoUrl) {
      greetingMoment.dismiss();
      setShowGreetingVideo(false);
      // Clip finished/skipped — reveal the full greeting text.
      setRevealingMsgId(null);
      setRevealedWords(9999);
    }
  };

  // Jun 2026 — voice/text sync + echo kill. When a lip-sync clip starts,
  // its own track is the ONLY audio (any in-flight TTS is terminated), and
  // the greeting bubble's words drip in step with the clip playback.
  const greetingClipShowing = !!greetingMoment.videoUrl
    && momentVideoUrl === greetingMoment.videoUrl && !playingFarewell;
  const handleMomentStarted = () => {
    // Fix D — if TTS already owns the greeting, a late clip must not
    // interrupt or rewind anything (defence in depth; the URL is already
    // suppressed above).
    if (greetingClipShowing && speechOwnerRef.current === 'tts') return;
    stopAllAudio();
    setIsSpeaking(false);
    if (greetingClipShowing && selectedAva?.ava_id) {
      setRevealingMsgId(`greeting_${selectedAva.ava_id}`);
      setRevealedWords(0);
    }
  };
  const handleMomentProgress = (ratio) => {
    if (!greetingClipShowing || !selectedAva?.ava_id) return;
    const gid = `greeting_${selectedAva.ava_id}`;
    const m = messages.find((x) => x.id === gid);
    if (!m) return;
    const words = (m.content || '').split(/\s+/).length;
    setRevealedWords(Math.max(1, Math.ceil(ratio * words)));
  };

  const handleRevealClose = () => {
    revealMoment.dismiss();
    try { localStorage.removeItem(`hibff_reveal_pending_${selectedAva?.ava_id}`); } catch (e) { /* no-op */ }
    setRevealPending(false);
  };

  // "Share my AvA" (founder spec): always opens the full share modal with
  // link box, copy action and social icons. Never auto-opens.
  const handleShareAva = () => setShareOpen(true);

  // Feb 2026 refactor — speech recognition (mic) lives in its own hook.
  // Transcript is streamed straight into the message input just like before.
  const {
    isListening,
    micError,
    micJustOpened,
    speechSupported,
    setMicError,
    start: startListeningHook,
    stop: stopListeningHook,
    recognitionRef,
    isListeningRef,
  } = useSpeechRecognition({ onTranscript: setMessage, suppressed: isSpeaking });

  // Empathic Handover System - passive monitoring on every message
  const handover = useEmpathicHandover();

  // Jun 2026 — lip-synced caring message the moment the handover consent step
  // opens (clip is pre-warmed per tutor, so it plays instantly). The clip
  // interrupts any TTS; declining before it plays disarms it.
  useEffect(() => {
    if (handover.showConsent && !handoverArmed) {
      fireMoment('handover', momentHandoverText());
      setHandoverRun((n) => n + 1);
      setHandoverArmed(true);
    } else if (!handover.showConsent && handoverArmed && !handoverMoment.videoUrl) {
      setHandoverArmed(false);
      handoverMoment.dismiss();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handover.showConsent]);
  useEffect(() => {
    if (handoverMoment.videoUrl) stopSpeaking?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoverMoment.videoUrl]);

  // Safeguarding correction (founder, Jun 2026): handover is OUT-OF-BAND
  // (responder dashboard + alert emails). No in-chat handover status widget
  // may exist — the draggable crisis consent modal is the ONLY
  // emergency-contact surface, and only when explicitly triggered.

  // ────────────────────────────────────────────────────────────────────
  // Unmount cleanup — silences AvA's voice the instant the user hits the
  // Back button (or any route change). The bulk of the audio lifecycle
  // (pause / detach / cancel speechSynthesis / beforeunload / pagehide)
  // is owned by `useAudioManager`. This effect handles the *other*
  // chat-specific resources that need to die alongside the audio:
  //   • revealIntervalRef  — word-drip timer (would otherwise keep
  //     firing setState on an unmounted component → React warning)
  //   • recognitionRef     — the mic SpeechRecognition session
  // ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Capture ref values at effect-setup time so cleanup uses THIS mount's
    // refs, not whatever the refs happen to point at when the component
    // later unmounts (the classic "stale ref in cleanup" React warning).
    const revealInterval = revealIntervalRef.current;
    const recognition = recognitionRef.current;
    const wasListening = isListeningRef.current;
    return () => {
      if (revealInterval) {
        clearInterval(revealInterval);
        revealIntervalRef.current = null;
      }
      if (recognition && wasListening) {
        try { recognition.stop(); } catch (e) { /* already stopped */ }
      }
    };
  }, [isListeningRef, recognitionRef]);

  // Fix 2 — load the last-message snippet per AvA thread once on mount.
  useEffect(() => {
    let cancelled = false;
    apiClient.request('/api/sessions/conversations/by-ava')
      .then((d) => { if (!cancelled) setConvByAva(d?.threads || {}); })
      .catch(() => { if (!cancelled) setConvByAva({}); });
    return () => { cancelled = true; };
  }, []);

  // Fix 2 — sidebar shows ONLY active tutors (those with conversation
  // history), plus the tutor currently being chatted with.
  const activeAvas = React.useMemo(() => {
    if (!convByAva) return [];
    return avas.filter((a) => convByAva[a.ava_id] || a.ava_id === selectedAva?.ava_id);
  }, [avas, convByAva, selectedAva?.ava_id]);

  // Redirect if no AvAs
  useEffect(() => {
    if (!avasLoading && avas.length === 0 && !selectedAva) {
      navigate('/conversations');
    }
  }, [avasLoading, avas, selectedAva, navigate]);

  // Resolve the active AvA and keep it in sync with fresh server data.
  // Fix (Sep 2026): personalising an AvA (new image/name/language) could
  // re-open showing the OLD portrait. Two causes: (1) we defaulted to
  // avas[0] / stale router state instead of the fresh server copy, and
  // (2) resolving by name-slug picked the WRONG AvA when two share a name
  // (e.g. a renamed tutor colliding with a built-in "Petra"). Fix: once an
  // AvA is selected, always adopt the fresh server copy of the SAME ava_id
  // (so edits reflect, never switching to a same-named AvA). Only use the
  // URL slug to pick the initial AvA on a cold deep-link with no selection.
  useEffect(() => {
    if (avas.length === 0) return;
    setSelectedAva(prev => {
      if (prev) {
        const fresh = avas.find(a => a.ava_id === prev.ava_id);
        return fresh || prev;
      }
      if (urlAvaSlug) {
        const bySlug = avas.find(a => a.ava_id === urlAvaSlug || avaSlug(a.name) === urlAvaSlug);
        if (bySlug) return bySlug;
      }
      return avas[0];
    });
  }, [avas, urlAvaSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Feb 2026 — consume `location.state.prefillMessage` when a dashboard
  // tile (e.g. "Ask Rebecca") deep-links into chat with a suggested
  // opener. We fill the input but DO NOT auto-send: the teen still
  // presses Send after they've read + optionally tweaked the prompt.
  useEffect(() => {
    const prefill = location.state?.prefillMessage;
    if (prefill && !message) {
      setMessage(prefill);
      // One-shot — clear the location.state.prefillMessage on the router
      // so a back/forward navigation doesn't repopulate the input.
      navigate(location.pathname, { replace: true, state: { ...location.state, prefillMessage: null } });
    }
    // Verified mount-only prefill: this consumes `location.state.prefillMessage`
    // exactly once when the chat page opens from a dashboard deep-link. Adding
    // `message`/`location`/`navigate` to deps would either re-fill after every
    // keystroke (losing the teen's edits) or re-fire on every route push,
    // trampling their in-progress draft.
  }, []);

  // Initialize chat session - with guard to prevent double execution
  // Jul 2026 refactor Session 2 — the 280-line inline effect that owned
  // this lives in `useChatSession` now. Behaviour unchanged; the dep-list
  // rationale (only `selectedAva?.ava_id`) is preserved inside the hook.
  useChatSession({
    selectedAva,
    user,
    interactionMode,
    isMuted,
    playTTS,
    stopAllAudio,
    setIsSpeaking,
    setMessages,
    setLoading,
    setCurrentSession,
    setHasGreeted,
    setAvaTyping,
    setRevealingMsgId,
    setRevealedWords,
    setShowGreetingVideo,
    greetedAvaRef,
    ttsPlayedIds,
    speechOwnerRef,
  });

  // Scroll to bottom — scoped to the transcript container ONLY.
  // scrollIntoView scrolled every scrollable ancestor (including the page),
  // dragging the layout up and clipping the AvA image + chat pane under the
  // top banner.
  useEffect(() => {
    const el = messagesEndRef.current;
    const container = el?.closest('.overflow-y-auto');
    if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  /**
   * Sanitize a message for TTS playback — strips URLs, markdown image
   * syntax, and raw https:// references so the AvA (esp. Rebecca) never
   * "reads out" a URL letter by letter. The full text (with URLs) still
   * lands in the visible chat bubble + AvAContextDisplay reference panel;
   * only the spoken audio is cleaned. Feb 2026.
   *
   * NOTE (2026-07-03 refactor): `sanitizeForTTS`, `playTTS` and
   * `stopSpeaking` now live inside `useAvAVoicePlayback`. The hook
   * exposes `playTTS` + `stopSpeaking` back to this page; the sanitiser
   * is internal to the hook. See `/app/frontend/src/hooks/useAvAVoicePlayback.js`.
   */

  const startListening = async () => {
    // Preserve the original page-level behaviour: cancel any live AvA
    // TTS before opening the mic, then delegate to the hook.
    if (isSpeaking) {
      window.speechSynthesis?.cancel();
      setIsSpeaking(false);
    }
    // Legacy countdown state — retained (currently unused) so the
    // suspended overlay can be re-enabled without state re-plumbing.
    micCountdownAbortRef.current = false;
    setMicCountdown(null);
    setMessage('');
    await startListeningHook();
  };

  const stopListening = () => {
    micCountdownAbortRef.current = true;
    setMicCountdown(null);
    stopListeningHook();
  };

  // Fix 2 — wipe the targeted tutor's thread (messages are keyed by ava_id
  // in db.conversations) and drop the tutor from the active sidebar list.
  const handleDeleteConversation = async () => {
    const target = deleteTarget || selectedAva;
    if (!target?.ava_id) {
      setShowDeleteConfirm(false);
      return;
    }
    setDeleting(true);
    try {
      await apiClient.request(
        `/api/sessions/conversations/${target.ava_id}`,
        { method: 'DELETE' }
      );
    } catch (e) {
      // 404 = nothing persisted yet — still safe to clear locally.
      console.error('Delete conversation failed:', e);
    }
    setConvByAva((prev) => {
      const next = { ...(prev || {}) };
      delete next[target.ava_id];
      return next;
    });
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
    setDeleting(false);
    if (target.ava_id === selectedAva?.ava_id) {
      // Wipe the local thread + greeting cache so the next AvA-select
      // produces a fresh greeting.
      setMessages([]);
      setHasGreeted(false);
      greetedAvaRef.current = null;
      ttsPlayedIds.current.clear();
      setCurrentSession(null);
      // Bounce to the library so they can pick again or restart cleanly.
      navigate('/conversations');
    }
  };

  // Jul 2026 refactor Session 2 — the 130-line inline handleSendMessage
  // that owned the outbound-message pipeline lives in `useChatSend` now.
  // Behaviour unchanged; the hook returns the same function reference so
  // the `<form onSubmit={handleSendMessage}>` binding at the render site
  // stays byte-identical.
  const { handleSendMessage } = useChatSend({
    message,
    messages,
    currentSession,
    selectedAva,
    user,
    interactionMode,
    isMuted,
    sending,
    setMessage,
    setMessages,
    setSending,
    setRevealingMsgId,
    setRevealedWords,
    revealIntervalRef,
    playTTS,
    handover,
  });


  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const REAL_WORLD_NUDGES = [
    "Great practice! Now try starting a real conversation with someone today.",
    "Nice session! Challenge yourself to compliment someone in person today.",
    "You're building great skills! Try using what you practised with a friend or colleague.",
    "Well done! The real magic happens when you take these skills into the real world.",
    "Good work! Why not reach out to someone you haven't spoken to in a while?",
  ];
  // Picked once per nudge-show (not per render) so the text doesn't shuffle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const nudgeText = useMemo(() => REAL_WORLD_NUDGES[Math.floor(Math.random() * REAL_WORLD_NUDGES.length)], [showNudge]);

  const handleEndSession = () => {
    stopSpeaking?.();
    // Play the cached lip-synced farewell (if ready) before leaving.
    if (farewellMoment.videoUrl && !playingFarewell) {
      stopSpeaking?.();
      setPlayingFarewell(true);
      return;
    }
    if (messages.length > 2) {
      setShowNudge(true);
    } else {
      navigate('/conversations');
    }
  };

  return (
    <DashboardLayout>
      {selectedAva?.name && (
        <PageMeta
          slug={`practice-${avaSlug(selectedAva.name)}`}
          path={`/conversations/practice/${avaSlug(selectedAva.name)}`}
          title={`Practice with ${selectedAva.name}`}
          description={`Practise difficult conversations with ${selectedAva.name}, your AI Practice Partner on HiBFF — before they happen in real life.`}
          noIndex
        />
      )}
      {/*
        Feb 2026 — Robert asked for the empty white space above the
        "Practice with Sam" header to be reduced. `.main-content` in
        dashboard.css applies `padding: 40px` globally, which is fine for
        cards-and-sections dashboard pages but leaves a chunky gap above
        the chat header. Negative `-mt-10` (40px) cancels that top padding
        and pulls the header up flush with the TopNav — chat is now
        edge-to-edge vertically, matching every modern messaging UX.
        Left/right padding is preserved so the two chat sidebars still
        breathe against the viewport edge.
      */}
      <div className="chat-screen-height flex flex-col -mt-10">
        {/* Tap-to-enlarge AvA animation modal — fires from mobile AvA bar */}
        {selectedAva && (
          <AvALightbox
            open={showAvaLightbox}
            onClose={() => setShowAvaLightbox(false)}
            name={selectedAva.name}
            avatarUrl={selectedAva.avatar_url}
            idleLoopUrl={idleLoopUrl}
          />
        )}
        {/* Header */}
        <div className="px-3 sm:px-8 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={handleEndSession}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="back-to-library"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-base sm:text-xl font-bold text-gray-900">
                Practice with {selectedAva?.name || 'AvA'}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Voice-enabled practice session</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => { setDeleteTarget(selectedAva); setShowDeleteConfirm(true); }}
              className="p-2 rounded-lg transition-colors bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600"
              data-testid="delete-conversation-btn"
              title="Delete conversation"
            >
              <Trash2 size={18} />
            </button>
            <button
              onClick={() => setShowShareModal(true)}
              className="p-2 rounded-lg transition-colors bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-600"
              data-testid="share-conversation-btn"
              title="Share this conversation"
            >
              <MessageCircle size={18} />
            </button>
            <VoiceMinutesBadge />
            <button
              onClick={() => setIsMuted(!isMuted)}
              className={`p-2 rounded-lg transition-colors ${isMuted ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="mute-btn"
            >
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden min-w-0">
          {/* AvA Sidebar - Jul 2026 Session 3 refactor — 106-line inline
              block replaced by presentational component. Hidden on mobile. */}
          <ChatAvaSidebar
            avas={activeAvas}
            avasLoading={avasLoading || convByAva === null}
            selectedAva={selectedAva}
            setSelectedAva={setSelectedAva}
            lastSnippets={convByAva || {}}
            onDeleteRequest={(ava) => { setDeleteTarget(ava); setShowDeleteConfirm(true); }}
          />

          {/* Main Content */}
          <div className="flex-1 flex flex-col lg:flex-row min-w-0 overflow-hidden">
            {selectedAva ? (
              <>
                {/* AvA Display - Jul 2026 Session 4 refactor — desktop+mobile
                    (~135 lines) extracted into ChatAvaDisplay. */}
                <ChatAvaDisplay
                  selectedAva={selectedAva}
                  idleLoopUrl={idleLoopUrl}
                  momentVideoUrl={momentVideoUrl}
                  onMomentEnd={handleMomentEnd}
                  onMomentStarted={handleMomentStarted}
                  onMomentProgress={handleMomentProgress}
                  isThinking={avaTyping || sending}
                  onShareAva={handleShareAva}
                  isSpeaking={isSpeaking}
                  sending={sending}
                  interactionMode={interactionMode}
                  setInteractionMode={setInteractionMode}
                  setShowAvaLightbox={setShowAvaLightbox}
                  messages={messages}
                />


                {/* Chat Area */}
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                  {/* Voice Note Nudge */}
                  <VoiceNoteCard />
                  {/* Neo journey progress banner (challenge AvA only) */}
                  {(selectedAva?.name || '').trim().toLowerCase() === 'neo' && (
                    <NeoJourneyBanner />
                  )}
                  {/* Messages - Jul 2026 Session 4 refactor — extracted to ChatTranscript. */}
                  <ChatTranscript
                    loading={loading}
                    messages={messages}
                    avaTyping={avaTyping}
                    sending={sending}
                    selectedAva={selectedAva}
                    interactionMode={interactionMode}
                    revealingMsgId={revealingMsgId}
                    revealedWords={revealedWords}
                    copiedMsgId={copiedMsgId}
                    setCopiedMsgId={setCopiedMsgId}
                    onSpeak={(m) => playTTS((m.content || '').trim(), m.id, { replay: true })}
                    setMessages={setMessages}
                    handover={handover}
                    navigate={navigate}
                    messagesEndRef={messagesEndRef}
                  />

                  {/* Composer input - Session 5 refactor — 102-line inline
                      block extracted into ChatComposer.jsx */}
                  <ChatComposer
                    message={message}
                    setMessage={setMessage}
                    handleSendMessage={handleSendMessage}
                    sending={sending}
                    speechSupported={speechSupported}
                    isListening={isListening}
                    isSpeaking={isSpeaking}
                    startListening={startListening}
                    stopListening={stopListening}
                    stopSpeaking={stopSpeaking}
                    selectedAvaName={selectedAva.name}
                    micError={micError}
                    setMicError={setMicError}
                  />
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
                  <p className="text-gray-500">Loading your AvA...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Safety Net (Dec 2026 — warm handover PAUSED by founder): the
          in-chat "talk to a real person" ask opens the SAME freephone
          helpline flow as the dashboard shield. HandoverConsentModal and
          the warm-handover backend stay in the codebase, dormant. */}
      {handover.showConsent && (
        <SafetyNetModal
          source="chat"
          emergency={handover.emergencyOverride}
          onClose={handover.declineHandover}
        />
      )}

      {/* Crisis contacts after a declined CRITICAL — teen always sees them */}
      {handover.crisisContacts && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-md bg-white border-2 border-red-300 rounded-2xl shadow-2xl p-4" data-testid="crisis-contacts-banner">
          <p className="text-sm text-gray-800 mb-2">{handover.crisisContacts.message}</p>
          <div className="space-y-1 text-sm">
            {handover.crisisContacts.contacts.map((c, i) => (
              <p key={i} className="text-red-700"><strong>{c.name}</strong> — {c.contact} <span className="text-gray-500 text-xs">({c.how})</span></p>
            ))}
          </div>
          <button onClick={handover.dismissCrisisContacts} className="mt-2 text-xs text-gray-500 underline" data-testid="crisis-contacts-dismiss">Got it</button>
        </div>
      )}

      {/* Post-Session Real-World Nudge */}
      {showNudge && (
        <div className="fixed bottom-6 inset-x-0 z-50 flex justify-center px-4 pointer-events-none" data-testid="post-session-nudge">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-md w-full p-6 text-center pointer-events-auto">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles size={28} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Great practice!</h3>
            <p className="text-gray-600 mb-6">
              {nudgeText}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => { setShowNudge(false); setShowShareModal(true); }}
                className="flex-1 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors inline-flex items-center justify-center gap-1.5"
                data-testid="nudge-share-btn"
              >
                <MessageCircle size={16} /> Share with a friend
              </button>
              <button
                onClick={() => { setShowNudge(false); navigate('/conversations'); }}
                className="flex-1 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
                data-testid="nudge-continue-btn"
              >
                I&apos;m on it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shareable Practice Modal */}
      <SharePracticeModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        avaName={selectedAva?.name}
        avaSlug={avaSlug(selectedAva?.name || '')}
      />

      {/* Sprint 207X+ — Milestone celebration toast (fires when challenge_run flips to completed) */}
      <MilestoneCelebrationToast
        onShare={() => setShowShareModal(true)}
        onMilestone={() => {
          fireMoment('milestone', MOMENT_MILESTONE_TEXT);
          setMilestoneRun((n) => n + 1);
          setMilestoneArmed(true);
        }}
      />

      {/* Session 5 refactor — Mic countdown + "listening now" cue combined
          into MicCountdownOverlay. NB: the pre-refactor page rendered
          both blocks TWICE (bug); now rendered once. */}
      <MicCountdownOverlay
        micCountdown={micCountdown}
        micJustOpened={micJustOpened}
        stopListening={stopListening}
      />

      {/* Sprint 207P — Delete conversation confirm modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[55]"
          data-testid="delete-conversation-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={26} className="text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
              Delete this conversation?
            </h3>
            <p className="text-sm text-gray-600 text-center mb-3">
              Your messages with {(deleteTarget || selectedAva)?.name || 'this AvA'} will be permanently
              erased from our servers. This can&apos;t be undone.
            </p>
            <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 text-center mb-6" data-testid="delete-privacy-guarantee">
              100% private: we hard-delete your conversation — no copies, no backups,
              nothing kept. Your data stays yours.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}
                disabled={deleting}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                data-testid="delete-conversation-cancel"
              >
                Keep
              </button>
              <button
                onClick={handleDeleteConversation}
                disabled={deleting}
                className="flex-1 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="delete-conversation-confirm"
              >
                {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* "Share my AvA" popup — link box, copy action, social icons */}
      <ShareAvaModal
        open={shareOpen}
        onClose={() => { setShareOpen(false); if (revealMoment.videoUrl) handleRevealClose(); }}
        avaName={selectedAva?.name}
        avatarUrl={selectedAva?.avatar_url}
        videoUrl={revealMoment.videoUrl || lastGreetingUrlRef.current || null}
      />
    </DashboardLayout>
  );
};

export default ConversationsChat;
